import type {
  CharacterIndexInput,
  CharacterIndexRowCellInput,
  CharacterIndexSourceAnchor,
  CharacterIndexSourceOffsets
} from "./types.js";

export const SINGLE_WITNESS_CHARACTER_EXTRACTION_PROMPT_VERSION =
  "single-witness-character-extraction-v1";

const SINGLE_WITNESS_CHARACTER_EXTRACTION_PROMPT_V1_HEADER = `# Deduplicated character extraction — classical Arabic narrative

## Goal
From the Arabic source text, extract a deduplicated list of **characters featured in the text**: each specific narrative participant, whether human, animal, or otherwise personified, who is referred to anywhere in the text. For each character, output exactly three fields — the verbatim name string used for the selected evidence, its IJMES transliteration, and a verbatim window of surrounding text. Output nothing else.

## What counts as a character
A character is any specific narrative participant, human or animal, identified through one or more referring expressions. This includes BOTH:

1. **Proper names** — e.g., ظهره.
2. **Definite descriptions, species terms, and epithets** used to refer to a character, *including ones that describe species, role, profession, rank, or kinship* — e.g., الأسد، الثور، ابن آوى، الحمامة، الغراب، الملك، الفيلسوف، التاجر، الأكّار، ابن الملك، ابن التاجر، المرأة الشريفة، الجارية، الغلام، الفتى.

Important: the cast of this text may be named by species term, epithet, role, or relationship rather than by proper name. You MUST extract these character references. Do not skip a character just because it is an animal, or because it is identified by species, role, or relationship. (The "do not return roles/relationships" line below concerns output *fields*, not which characters to extract — see "Output exactly three fields.")

## What does NOT count (never extract these)
- **Placeholder stand-ins for unnamed people:** فلان and فلانة, and any construct built on them (امرأة فلان، مولاتي فلانة، ابن فلان…). These are literary dummies meaning "so-and-so" / "a certain person." Never emit فلان or فلانة as a name. If a character is referred to by such a placeholder, capture that character only through its other, contentful references (e.g., امرأة شريفة), if the text provides any.
- **Place names and other non-character entities** — e.g., مطور، قرنونا. Never extract these.
- **Generic animals or collectives that are not specific narrative participants** — do not extract an animal reference merely because it names a species; extract it only when the text uses it as a character reference.
- The truncation marker \`[...]\` (see "The [...] marker").

## Output exactly three fields per character — and nothing more
For each character emit only: \`name\`, \`transliteration\`, \`window\`.
Do NOT add any other field or value — no id, no folio/location reference, no role label, no relationship ("brother of the king", "father of X"), no spelling variants, no normalized form, no notes, no commentary. This is the entire meaning of the original "do not return roles, relationships, variants…" instruction: **do not add extra fields.** It is NOT a filter on which people to extract.

### name
Copy the selected referring expression verbatim from the text — same letters, same diacritics (tashkīl), same spelling. Include the article if present (الأكّار, not أكّار). Do not normalize or correct.

### transliteration — IJMES
Romanize \`name\` using IJMES (IJMES Transliteration Chart) conventions:
- ء → ʾ ; ع → ʿ ; ث → th ; ج → j ; ح → ḥ ; خ → kh ; ذ → dh ; ش → sh ; ص → ṣ ; ض → ḍ ; ط → ṭ ; ظ → ẓ ; غ → gh ; ق → q
- Long vowels: ā, ī, ū
- Definite article is **al-**, **not** assimilated to sun letters: write \`al-tājir\`, not \`at-tājir\`
- tāʾ marbūṭa → \`-a\` (or \`-at\` in iḍāfa)
- Transliterate the name *as written*; do not translate it (التاجر → \`al-tājir\`, not "the merchant")

### window
A contiguous span copied **verbatim** from the source, centered on the name, giving about one line of context (≈ 8–15 words, or whatever standard window length your pipeline uses):
- Reproduce exactly — same letters, diacritics, spelling, and word forms. Do not normalize, correct, add/remove diacritics, or re-segment.
- The \`name\` string must appear inside the \`window\` exactly as written.
- If a visible bracketed row label is useful for locating the evidence, include it in the \`window\`.
- If a \`[...]\` marker falls inside the span, end the window at the marker; do not cross it.

## The [...] marker
The source may contain \`[...]\`. It marks text already captured by earlier windows. Ignore the marker itself and extract characters only from the visible text around it. Never treat \`[...]\` as a character, and never include it in a name or window.

## One object per character
Emit one object per **deduplicated character**, not one per occurrence. If a character is referred to ten times, that is one object with one verbatim evidence window. If the same character is referred to by multiple surface forms, choose the clearest and most specific contentful referring expression available in the text. If several are equally specific, use the first occurrence.

## Examples (drawn from this text)

Extract:
\`\`\`json
{ "name": "ظهره",    "transliteration": "Ẓahra",       "window": "أنا ابن فلان الملك ملك قرنونا واسمي ظهره كنت وُلّيت عهد أبي فلمّا مات" }
{ "name": "الأكّار",  "transliteration": "al-akkār",     "window": "فقالوا للأكّار المكين انطلق فاطلب لنا باجتهادك طعامًا ليومنا هذا" }
{ "name": "ابن الملك","transliteration": "ibn al-malik", "window": "والمثل في ذلك مثل ابن الملك الذي رُئي على باب مدينة يقال لها مطور جالسًا" }
\`\`\`

Do NOT extract:
- فلان / فلانة in \`مولاتي فلانة امرأة فلان\` — placeholders. (The woman is captured through امرأة شريفة / المرأة instead.)
- مطور، قرنونا — place names.

## Output
Return JSON only — no markdown fences, no preamble, no trailing text:
\`\`\`json
{ "characters": [ { "name": "...", "transliteration": "...", "window": "..." } ] }
\`\`\``;

export type CharacterExtractionScope =
  | CharacterExtractionWitnessScope
  | CharacterExtractionSceneScope
  | CharacterExtractionRowsScope;

export interface CharacterExtractionWitnessScope {
  readonly kind: "witness";
  readonly witnessId: string;
}

export interface CharacterExtractionSceneScope {
  readonly kind: "scene";
  readonly witnessId: string;
  readonly sceneId: string;
}

export interface CharacterExtractionRowsScope {
  readonly kind: "rows";
  readonly witnessId: string;
  readonly rowIds: readonly string[];
}

export interface PrepareSingleWitnessCharacterExtractionOptions {
  readonly promptVersion?: string;
}

export interface PreparedCharacterExtractionScopeMetadata {
  readonly witnessId: string;
  readonly kind: CharacterExtractionScope["kind"];
  readonly selectedRowIds: readonly string[];
  readonly sceneId?: string;
  readonly requestedRowIds?: readonly string[];
}

export interface PreparedSingleWitnessCharacterExtractionCall {
  readonly promptText: string;
  readonly sourceText: string;
  readonly sourceMap: readonly CharacterExtractionSourceMapSpan[];
  readonly scope: PreparedCharacterExtractionScopeMetadata;
  readonly promptVersion: string;
}

export type CharacterExtractionSourceMapSpanKind = "row-label" | "text";

export interface CharacterExtractionSourceMapSpan {
  readonly start: number;
  readonly end: number;
  readonly spanKind: CharacterExtractionSourceMapSpanKind;
  readonly witnessId: string;
  readonly rowId: string;
  readonly rowLabel: string;
  readonly unitUuid?: string | null;
  readonly unitLabel?: string | null;
  readonly unitId?: string | null;
  readonly localStart?: number | null;
  readonly localEnd?: number | null;
  readonly sourceOffsets?: CharacterIndexSourceOffsets;
}

export interface PreparedCharacterExtractionValidationIssue {
  readonly code:
    | "empty-source-text"
    | "empty-source-segment"
    | "invalid-source-map-range"
    | "non-monotonic-source-map";
  readonly message: string;
  readonly spanIndex?: number;
  readonly rowId?: string;
  readonly witnessId?: string;
}

interface SelectedTextSegment {
  readonly rowId: string;
  readonly rowLabel: string;
  readonly witnessId: string;
  readonly cell: CharacterIndexRowCellInput;
}

const normalizeSelectedRowIds = (rowIds: readonly string[]): Set<string> => (
  new Set(rowIds.map((rowId) => rowId.trim()).filter(Boolean))
);

const getScopeWitnessId = (scope: CharacterExtractionScope): string => scope.witnessId;

const rowMatchesScope = (
  row: CharacterIndexInput["alignedRows"][number],
  scope: CharacterExtractionScope,
  requestedRowIdSet: ReadonlySet<string> | null = null
): boolean => {
  if (scope.kind === "witness") {
    return true;
  }
  if (scope.kind === "scene") {
    return row.sceneId === scope.sceneId;
  }
  return (requestedRowIdSet || normalizeSelectedRowIds(scope.rowIds)).has(row.rowId);
};

const findSelectedSegments = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope
): SelectedTextSegment[] => {
  const witnessId = getScopeWitnessId(scope);

  if (!input.witnesses.some((witness) => witness.witnessId === witnessId)) {
    throw new Error(`Cannot prepare character extraction call: unknown witness ${witnessId}.`);
  }

  const segments: SelectedTextSegment[] = [];
  const requestedRowIdSet = scope.kind === "rows" ? normalizeSelectedRowIds(scope.rowIds) : null;
  for (const row of input.alignedRows) {
    if (!rowMatchesScope(row, scope, requestedRowIdSet)) {
      continue;
    }
    for (const cell of row.cells) {
      if (cell.witnessId !== witnessId || cell.gap || cell.text.trim().length === 0) {
        continue;
      }
      segments.push({
        rowId: row.rowId,
        rowLabel: row.rowLabel,
        witnessId,
        cell
      });
    }
  }
  return segments;
};

const appendSpan = (
  parts: string[],
  sourceMap: CharacterExtractionSourceMapSpan[],
  sourceTextLength: number,
  text: string,
  span: Omit<CharacterExtractionSourceMapSpan, "start" | "end">
): number => {
  if (!text) {
    return sourceTextLength;
  }
  const start = sourceTextLength;
  const end = start + text.length;
  parts.push(text);
  sourceMap.push({
    ...span,
    start,
    end
  });
  return end;
};

const appendRaw = (
  parts: string[],
  sourceTextLength: number,
  text: string
): number => {
  parts.push(text);
  return sourceTextLength + text.length;
};

const buildSourceTextAndMap = (
  segments: readonly SelectedTextSegment[]
): {
  readonly sourceText: string;
  readonly sourceMap: readonly CharacterExtractionSourceMapSpan[];
  readonly selectedRowIds: readonly string[];
} => {
  const parts: string[] = [];
  const sourceMap: CharacterExtractionSourceMapSpan[] = [];
  const selectedRowIds: string[] = [];
  const selectedRowIdSet = new Set<string>();
  let sourceTextLength = 0;
  let previousRowId: string | null = null;

  for (const segment of segments) {
    if (previousRowId !== null) {
      sourceTextLength = appendRaw(parts, sourceTextLength, "\n");
    }

    if (segment.rowId !== previousRowId) {
      if (!selectedRowIdSet.has(segment.rowId)) {
        selectedRowIds.push(segment.rowId);
        selectedRowIdSet.add(segment.rowId);
      }
      const label = `[${segment.rowLabel}]`;
      sourceTextLength = appendSpan(parts, sourceMap, sourceTextLength, label, {
        spanKind: "row-label",
        witnessId: segment.witnessId,
        rowId: segment.rowId,
        rowLabel: segment.rowLabel,
        localStart: null,
        localEnd: null
      });
      sourceTextLength = appendRaw(parts, sourceTextLength, "\n");
      previousRowId = segment.rowId;
    }

    const textStart = sourceTextLength;
    sourceTextLength = appendSpan(parts, sourceMap, sourceTextLength, segment.cell.text, {
      spanKind: "text",
      witnessId: segment.witnessId,
      rowId: segment.rowId,
      rowLabel: segment.rowLabel,
      ...(segment.cell.unitUuid ? { unitUuid: segment.cell.unitUuid } : {}),
      ...(segment.cell.unitLabel ? { unitLabel: segment.cell.unitLabel } : {}),
      ...(segment.cell.unitId ? { unitId: segment.cell.unitId } : {}),
      localStart: 0,
      localEnd: segment.cell.text.length,
      ...(segment.cell.sourceOffsets ? { sourceOffsets: segment.cell.sourceOffsets } : {})
    });
    if (sourceTextLength === textStart) {
      previousRowId = segment.rowId;
    }
  }

  return {
    sourceText: parts.join(""),
    sourceMap,
    selectedRowIds
  };
};

const buildScopeMetadata = (
  scope: CharacterExtractionScope,
  selectedRowIds: readonly string[]
): PreparedCharacterExtractionScopeMetadata => ({
  witnessId: scope.witnessId,
  kind: scope.kind,
  selectedRowIds,
  ...(scope.kind === "scene" ? { sceneId: scope.sceneId } : {}),
  ...(scope.kind === "rows" ? { requestedRowIds: [...scope.rowIds] } : {})
});

const buildPromptText = (sourceText: string): string => (
  `${SINGLE_WITNESS_CHARACTER_EXTRACTION_PROMPT_V1_HEADER}\n\n---\n\nTEXT\n${sourceText}`
);

export const prepareSingleWitnessCharacterExtractionCall = (
  input: CharacterIndexInput,
  scope: CharacterExtractionScope,
  options: PrepareSingleWitnessCharacterExtractionOptions = {}
): PreparedSingleWitnessCharacterExtractionCall => {
  const segments = findSelectedSegments(input, scope);
  const { sourceText, sourceMap, selectedRowIds } = buildSourceTextAndMap(segments);
  const promptVersion = options.promptVersion || SINGLE_WITNESS_CHARACTER_EXTRACTION_PROMPT_VERSION;

  return {
    promptText: buildPromptText(sourceText),
    sourceText,
    sourceMap,
    scope: buildScopeMetadata(scope, selectedRowIds),
    promptVersion
  };
};

export const validatePreparedExtractionCall = (
  call: PreparedSingleWitnessCharacterExtractionCall
): PreparedCharacterExtractionValidationIssue[] => {
  const issues: PreparedCharacterExtractionValidationIssue[] = [];

  if (call.sourceText.trim().length === 0) {
    issues.push({
      code: "empty-source-text",
      message: "Prepared extraction call has no non-empty source text."
    });
  }

  let previousEnd = 0;
  call.sourceMap.forEach((span, spanIndex) => {
    if (
      !Number.isInteger(span.start) ||
      !Number.isInteger(span.end) ||
      span.start < 0 ||
      span.end > call.sourceText.length ||
      span.end <= span.start
    ) {
      issues.push({
        code: "invalid-source-map-range",
        message: "Source map span range must be non-empty and within sourceText length.",
        spanIndex,
        rowId: span.rowId,
        witnessId: span.witnessId
      });
    }

    if (span.start < previousEnd) {
      issues.push({
        code: "non-monotonic-source-map",
        message: "Source map spans must be monotonic and non-overlapping.",
        spanIndex,
        rowId: span.rowId,
        witnessId: span.witnessId
      });
    }
    previousEnd = Math.max(previousEnd, span.end);

    if (span.spanKind === "text" && call.sourceText.slice(span.start, span.end).trim().length === 0) {
      issues.push({
        code: "empty-source-segment",
        message: "Source text segments selected for extraction must be non-empty.",
        spanIndex,
        rowId: span.rowId,
        witnessId: span.witnessId
      });
    }
  });

  return issues;
};

export const mapValidatedWindowRangeToSourceAnchors = (
  call: Pick<PreparedSingleWitnessCharacterExtractionCall, "sourceText" | "sourceMap">,
  range: { readonly start: number; readonly end: number }
): CharacterIndexSourceAnchor[] => {
  if (
    !Number.isInteger(range.start) ||
    !Number.isInteger(range.end) ||
    range.start < 0 ||
    range.end > call.sourceText.length ||
    range.end <= range.start
  ) {
    throw new Error("Cannot map validated window range: range must be within sourceText.");
  }

  return call.sourceMap.flatMap((span) => {
    if (span.spanKind !== "text") {
      return [];
    }
    const overlapStart = Math.max(range.start, span.start);
    const overlapEnd = Math.min(range.end, span.end);
    if (overlapEnd <= overlapStart) {
      return [];
    }

    const spanLocalStart = span.localStart || 0;
    const localStart = spanLocalStart + (overlapStart - span.start);
    const localEnd = spanLocalStart + (overlapEnd - span.start);
    const sourceOffsets = span.sourceOffsets
      ? {
          start: span.sourceOffsets.start + (overlapStart - span.start),
          end: span.sourceOffsets.start + (overlapEnd - span.start)
        }
      : undefined;

    return [{
      witnessId: span.witnessId,
      rowId: span.rowId,
      ...(span.unitUuid ? { unitUuid: span.unitUuid } : {}),
      ...(span.unitLabel ? { unitLabel: span.unitLabel } : {}),
      ...(span.unitId ? { unitId: span.unitId } : {}),
      localStart,
      localEnd,
      ...(sourceOffsets ? { sourceOffsets } : {}),
      window: call.sourceText.slice(overlapStart, overlapEnd)
    }];
  });
};
